import React, { useContext } from 'react';
import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Alert, Space, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import UbiquitiHostWifiBroadcastsContext from '@totem/components/ubiquiti/hosts/details/wifiBroadcasts/ubiquitiHostWifiBroadcastsContext';
import {
  UbiquitiApiDataItem,
  UbiquitiWifiBroadcast,
} from '@totem/components/ubiquiti/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBTableExportButton from '@totem/components/ui_components/antDesignOverrides/IBTableExportButton';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const UbiquitiHostWifiBroadcastsTable = () => {
  const { loading, error, wifiBroadcasts } = useContext(
    UbiquitiHostWifiBroadcastsContext,
  );

  const columns: ColumnProps<UbiquitiApiDataItem<UbiquitiWifiBroadcast>>[] = [
    {
      title: 'Name',
      dataIndex: ['data', 'name'],
      key: 'name',
      sorter: (compA, compB) =>
        sortStringAscending(compA.data.name, compB.data.name),
    },
    {
      title: 'Enabled',
      dataIndex: ['data', 'enabled'],
      key: 'enabled',
      render: (_, record: UbiquitiApiDataItem<UbiquitiWifiBroadcast>) =>
        record.data.enabled ? (
          <CheckCircleOutlined style={{ color: 'green' }} />
        ) : (
          <MinusCircleOutlined />
        ),
    },
    {
      title: 'Security',
      dataIndex: ['data', 'securityConfiguration', 'type'],
      key: 'security',
      render: (_, record: UbiquitiApiDataItem<UbiquitiWifiBroadcast>) => (
        <Tag>{record.data.securityConfiguration.type}</Tag>
      ),
    },
    {
      title: 'Frequencies',
      dataIndex: ['data', 'broadcastingFrequenciesGHz'],
      key: 'broadcastingFrequenciesGHz',
      render: (_, record: UbiquitiApiDataItem<UbiquitiWifiBroadcast>) => (
        <Space size={4}>
          {(record.data.broadcastingFrequenciesGHz ?? []).map((frequency) => (
            <Tag key={frequency}>{frequency} GHz</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Broadcasting Devices',
      dataIndex: ['data', 'broadcastingDeviceFilter', 'deviceIds'],
      key: 'broadcastingDevices',
      render: (_, record: UbiquitiApiDataItem<UbiquitiWifiBroadcast>) =>
        record.data.broadcastingDeviceFilter.deviceIds?.length ?? 0,
    },
  ];

  const exportHeaders = [
    'Name',
    'Enabled',
    'Security',
    'Frequencies',
    'Broadcasting Devices',
  ];
  const exportRows = wifiBroadcasts.map((broadcast) => [
    broadcast.data.name,
    broadcast.data.enabled ? 'Yes' : 'No',
    broadcast.data.securityConfiguration.type,
    (broadcast.data.broadcastingFrequenciesGHz ?? [])
      .map((frequency) => `${frequency} GHz`)
      .join('; '),
    broadcast.data.broadcastingDeviceFilter.deviceIds?.length ?? 0,
  ]);

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="WiFi broadcasts unavailable"
        description="No WiFi broadcast data could be found for this host."
      />
    );
  }

  return (
    <IBTable<UbiquitiApiDataItem<UbiquitiWifiBroadcast>>
      showSorterTooltip
      columns={columns}
      dataSource={wifiBroadcasts}
      loading={loading}
      pagination={{ defaultPageSize: 10, showSizeChanger: true }}
      rowKey={(record) => record.data.id}
      footerLeft={
        <IBTableExportButton
          fileName="wifi-broadcasts"
          headers={exportHeaders}
          rows={exportRows}
        />
      }
    />
  );
};

export default UbiquitiHostWifiBroadcastsTable;
